import type { Partner } from "@expert/shared-types";
import type React from "react";

import { freezeAppConfigSingleton, useAppConfigAndServiceProviderOverride } from "@expert/app-config";
import { useNotification } from "@expert/common-ui";
import { partnerEnabledForExpertUniversity } from "@expert/expert-university";
import { enableWorkerLogging, getLogger } from "@expert/logging";
import {
    getAllowedPartnersOverride,
    getServiceProvider,
    getServiceProviderOverrideFromQueryParams,
} from "@expert/service-providers";
import { ExpertWorkspaceWebSocketProvider } from "@expert/websocket";
import { Flex } from "@mantine/core";
import { useNetwork } from "@mantine/hooks";
import { type ErrorInfo, useEffect, useMemo, useRef } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

import { ConnectCallbackTimeoutError } from "@/amazon-connect/withCallbacks";
import { useAnalytics } from "@/analytics";
import { useSetupSsoToken } from "@/auth";
import { GlobalError } from "@/common-ui";
import { WorkspaceLoader } from "@/common-ui/WorkspaceLoader";
import { Workspace } from "@/components";
import { PartnerAwareWebsocketProvider } from "@/components/gaia/PartnerAwareWebsocketProvider";
import { useSimplrShadowAnalytics } from "@/components/gaia/useSimplrShadowAnalytics";
import { env } from "@/config";
import { WorkspaceFeatureFlags } from "@/features";
import { AgentSdkProvider } from "@/sdk/agent/AgentSdkProvider";
import { useAgentAttributesStore } from "@/sdk/agent/store/agent-attributes.store";
import { getAgentLocation } from "@/sdk/agent/utils/getAgentLocation";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { errorEventBus, getSharedApiClient, useFeatureFlag, useFirstLoadPending, userCache } from "@/shared-utils";
import { USER_FRIENDLY_NAME_QS_KEY } from "@/shared-utils/constants";
import { emitWorkspaceShellCrash } from "@/shared-utils/workspaceShellCrashMetric";

const logger = getLogger({ module: "Home" });

// The softphone outlives this fallback, so Refresh re-renders the workspace instead of reloading the page,
// which would drop a live call's audio.
function renderWorkspaceCrash({ resetErrorBoundary }: FallbackProps) {
    return <GlobalError msg="Refresh to load this screen again." onRefresh={resetErrorBoundary} />;
}

function reportWorkspaceCrash(error: unknown, { componentStack }: ErrorInfo) {
    const errorMessage = error instanceof Error ? error.message : undefined;
    logger.fatal({ error, errorMessage, componentStack }, "Workspace error boundary caught an error");
    emitWorkspaceShellCrash("workspace");
}

export default function Home(): React.JSX.Element {
    const logger = useLogger({ module: "Home" });
    const environment = import.meta.env.VITE_ENV_TYPE as string;
    const employeeId = userCache.employeeId ?? undefined;
    const userFriendlyName =
        environment === "development"
            ? (new URLSearchParams(window.location.search).get(USER_FRIENDLY_NAME_QS_KEY) ?? undefined)
            : undefined;
    const allowedPartnersOverride = getAllowedPartnersOverride();
    const notification = useNotification();
    const { dispatcher } = useAnalytics();
    const auth = useSetupSsoToken(userFriendlyName);
    const { data: isWebsocketEnabled = true, isLoading: isLoadingWebsocketEnabled } = useFeatureFlag(
        "EWP-isWebsocketEnabled",
        {
            org: auth.user?.profile.org,
        },
    );
    const { data: isInExpertUniversityPilot } = useFeatureFlag("isInExpertUniversityPilot");

    // TODO: Remove Twilio from here
    const {
        data: [appConfig, serviceProviderOverride] = [],
        isLoading: appConfigLoading,
        error: appConfigError,
        status: appConfigAndServiceProviderOverrideStatus,
    } = useAppConfigAndServiceProviderOverride(getSharedApiClient(), employeeId, env.channel);

    const partner = allowedPartnersOverride?.length
        ? allowedPartnersOverride[0]
        : (appConfig?.allowedPartners.split(",")[0] as Partner | undefined);

    // Redesign v2 pilot (the Expert Hub voice workspace). The flag is partner-targeted, so a
    // context-less query would always evaluate to the `false` default. Drilled down to
    // VoiceWorkspaceContent and loading-gated below so the workspace never paints the wrong layout
    // (no legacy → redesigned flash).
    const { data: isVoiceWorkspaceRedesignV2Enabled = false, isLoading: isLoadingVoiceWorkspaceRedesignV2Enabled } =
        useFeatureFlag<boolean>("EWP-isVoiceWorkspaceRedesignV2Enabled", { partner });

    // Dark launch: when the flag is on for this expert (its config rules scope the pilot cohort; prod
    // targets specific experts, nonprod is broader), open the Simplr websocket in shadow mode alongside
    // GAIA and emit an analytic for any message difference. GAIA stays the source of truth; the shadow
    // never drives the UI.
    const { data: isSimplrWebsocketDarkLaunchEnabled = false } = useFeatureFlag<boolean>(
        "isSimplrWebsocketDarkLaunchEnabled",
        { partner },
    );

    const { onShadowComparison, onShadowSummary } = useSimplrShadowAnalytics();

    // The feature flag is the authoritative gate: its config rules scope the cohort (default false for
    // everyone else), so the shadow runs only for the targeted pilot. A configured Simplr URL is also
    // required (without one there is nothing to connect to), which keeps the shadow off in any build whose
    // env omits VITE_SIMPLR_WEBSOCKET_URL. Not gated on `!isProdMode`, so the prod dark launch can activate.
    const isSimplrShadowEnabled = isSimplrWebsocketDarkLaunchEnabled && Boolean(env.simplrWebsocketUrl);

    // Chat redesign v2 pilot (the Expert Hub chat workspace) — resolved here against the expert's partner
    // (not the active chat's), so the workspace layout stays stable as the expert switches between
    // concurrent chats. Drilled down and gated on its loading state below to avoid a legacy → redesigned
    // flash, mirroring voice.
    const { data: isChatWorkspaceRedesignV2Enabled = false, isLoading: isLoadingChatWorkspaceRedesignV2Enabled } =
        useFeatureFlag<boolean>("EWP-isChatWorkspaceRedesignV2Enabled", { partner });

    // Latch first resolution so a later flag re-fetch (these flags are partner/org-keyed) can't re-show
    // the loader and unmount AgentSdkProvider mid-session, which would re-init the Amazon Connect SDK
    // and drop its contact-event subscriptions. The resolved value still flows via its own prop.
    const isWebsocketFlagFirstLoad = useFirstLoadPending(isLoadingWebsocketEnabled);
    const isVoiceRedesignV2FirstLoad = useFirstLoadPending(isLoadingVoiceWorkspaceRedesignV2Enabled);
    const isChatRedesignV2FirstLoad = useFirstLoadPending(isLoadingChatWorkspaceRedesignV2Enabled);

    /**
     * Priority order is:
     * 1. `ServiceProviderOverride` feature flag
     * 2. `serviceProvider` query param (nonprod only)
     * 3. `CLOUD_SPECIFIC_SERVICE_PROVIDER` env var (chat only)
     * 4. static provider mapping by partner
     * 5. `AMAZON_CONNECT` default
     */
    const serviceProvider = useMemo(() => {
        // The dev-only MOCK provider must win over the ServiceProviderOverride flag (which resolves to a
        // real provider in nonprod). getServiceProviderOverrideFromQueryParams is IS_DEV-gated → inert in prod.
        if (getServiceProviderOverrideFromQueryParams() === "MOCK") return "MOCK";
        return serviceProviderOverride ?? getServiceProvider(partner, env.channel);
    }, [partner, serviceProviderOverride]);

    const isTrainingModeAllowed =
        serviceProvider === "AMAZON_CONNECT" &&
        partner &&
        partnerEnabledForExpertUniversity(partner, env.channel, isInExpertUniversityPilot);

    useEffect(() => {
        /**
         * Wait for feature flag to finish loading before saving resolved service providers to localStorage
         * This is to avoid briefly setting the wrong service provider in localStorage during the loading process.
         */
        if (appConfigAndServiceProviderOverrideStatus === "success" && env.channel === "chat") {
            localStorage.setItem("serviceProvider", serviceProvider);
        }
    }, [appConfigAndServiceProviderOverrideStatus, serviceProvider]);

    const partnerConfig =
        appConfig && partner && appConfig.partnersConfig[partner in appConfig.partnersConfig ? partner : "others"];

    useEffect(() => {
        const errorUnsub = errorEventBus.on("error-occurred", (error) => {
            logger.info({ error }, "Error notification shown in UI.");

            if (error instanceof ConnectCallbackTimeoutError) {
                notification.error(
                    "The call is taking longer than expected to complete. Please refresh the page and try again.",
                );
            } else if (error instanceof Error) {
                notification.error(`An unexpected error occurred: ${error.message}`);
            } else {
                notification.error("An unexpected error occurred");
            }
        });

        return () => {
            errorUnsub();
        };
    }, [logger, notification]);

    useFeatureFlag("EWP-isWorkerLoggingEnabled", undefined, {
        onResponse: (isWorkerLoggingEnabled = false) => {
            if (isWorkerLoggingEnabled) enableWorkerLogging();
        },
    });

    const handlePageReload = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    };

    useEffect(() => {
        const getAgentSite = async () => {
            const { locationId: site } = await getAgentLocation(logger);
            useAgentAttributesStore.getState().setAgentSite(site);
        };
        void getAgentSite();
    }, [logger]);

    useEffect(() => {
        if (!auth.user && auth.isLoading) {
            void dispatcher.dispatchBusinessEvent("Authenticating");
        }
    }, [dispatcher, auth.user, auth.isLoading]);

    // normally, `useState()` is desirable but in this case we need to change the value and read it before React rerenders
    const isAuthRedirectNeededRef = useRef(false);
    function handleOnAuthRedirect() {
        logger.warn("Redirecting for agent sdk re-authentication");
        isAuthRedirectNeededRef.current = true;
    }

    const networkStatus = useNetwork();
    useEffect(() => {
        logger.info(networkStatus, "Network status");
    }, [logger, networkStatus]);

    if (!auth.error && auth.isLoading) {
        return <WorkspaceLoader msg="Acquiring access token..." />;
    }

    // WARNING: add loading flags to this gate only via useFirstLoadPending (see the latch block above).
    // While any condition here is true the whole provider subtree (incl. AgentSdkProvider) is unmounted,
    // so a raw context-keyed flag whose isLoading re-enters true on a key change (e.g. partner/org)
    // would remount and re-initialize the Amazon Connect SDK mid-session, dropping its contact-event
    // subscriptions (a trigger for the stuck-"Connecting" / missing dial pad class).
    if (appConfigLoading || isWebsocketFlagFirstLoad || isVoiceRedesignV2FirstLoad || isChatRedesignV2FirstLoad) {
        return <WorkspaceLoader msg="Checking if experimental features should be enabled..." />;
    }

    if (auth.error || !auth.user) {
        logger.fatal({ error: auth.error }, "Error authenticating");
        return (
            <GlobalError
                msg="You can also contact Service Desk for help or if it’s your first time using Expert Workspace."
                onRefresh={handlePageReload}
            />
        );
    }

    if (!appConfigError && (appConfigLoading || !appConfig)) {
        void dispatcher.dispatchAction("LoadingConfiguration", "LoadingConfiguration");
        return <WorkspaceLoader msg="Loading configuration..." />;
    }

    if ((!appConfigLoading && !appConfig) || appConfigError) {
        logger.fatal({ error: appConfigError }, "Error loading configuration");
        return <GlobalError title="Error loading Workspace configuration" onRefresh={handlePageReload} />;
    }

    freezeAppConfigSingleton({ appConfig: appConfig!, org: userCache.org, channel: env.channel });

    const { ssoAccessToken, identity } = userCache;
    if (!ssoAccessToken) return <h1>DEBUG: NO token</h1>;
    if (!identity) return <h1>DEBUG: NO identity</h1>;
    if (!employeeId) {
        logger.fatal({ userCache }, "No employee ID for user.");
        return <h1>DEBUG: NO employee ID</h1>;
    }

    return (
        <Flex flex={1} direction="column" h="100%" w="100%">
            {/* TODO: Move relevant providers up once we have several pages */}
            <ExpertWorkspaceWebSocketProvider
                identity={employeeId}
                token={ssoAccessToken}
                webSocketUrl={env.backendUrls.expertWorkspaceWebSocketUrl}
                isWebsocketEnabled={isWebsocketEnabled}
            >
                <AgentSdkProvider
                    employeeNum={employeeId}
                    userFriendlyName={userFriendlyName}
                    serviceProvider={serviceProvider}
                    partnerConfig={partnerConfig}
                    onAuthRedirect={handleOnAuthRedirect}
                    isTrainingModeAllowed={isTrainingModeAllowed}
                >
                    {/* The boundary sits directly inside AgentSdkProvider, which renders the CCP container beside its
                        children, so a render crash below here keeps the softphone (and a live call's audio) mounted. */}
                    <ErrorBoundary fallbackRender={renderWorkspaceCrash} onError={reportWorkspaceCrash}>
                        {/* Live realtime connection: routes to the Simplr websocket in place of GAIA when the
                        isSimplrWebsocketLiveEnabled flag targets the active call, otherwise GAIA. The Simplr shadow
                        dark launch (comparison-only) is passed through and is suppressed automatically once the live
                        connection is already Simplr. */}
                        <PartnerAwareWebsocketProvider
                            identity={identity}
                            token={ssoAccessToken}
                            logger={logger}
                            shadowUrl={env.simplrWebsocketUrl}
                            shadowEnabled={isSimplrShadowEnabled}
                            onShadowComparison={onShadowComparison}
                            onShadowSummary={onShadowSummary}
                        >
                            <WorkspaceFeatureFlags employeeId={employeeId} logger={logger}>
                                <Workspace
                                    isAuthRedirectNeededRef={isAuthRedirectNeededRef}
                                    isVoiceWorkspaceRedesignV2Enabled={isVoiceWorkspaceRedesignV2Enabled}
                                    isChatWorkspaceRedesignV2Enabled={isChatWorkspaceRedesignV2Enabled}
                                />
                            </WorkspaceFeatureFlags>
                        </PartnerAwareWebsocketProvider>
                    </ErrorBoundary>
                </AgentSdkProvider>
            </ExpertWorkspaceWebSocketProvider>
        </Flex>
    );
}
